import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { apiClient, type ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface Team {
  id: string;
  name: string;
  isActive: boolean;
}

interface DataRole {
  id: string;
  name: string;
  isActive: boolean;
  isFullAccess: boolean;
}

interface TeamDataRole {
  dataRoleId: string;
  assignedAt: string;
}

// ─── Main Component ─────────────────────────────────────────────────────────────

/**
 * AssignmentsTab — Matrix showing teams ↔ data roles with toggle switches.
 * Improved layout with search filters and scrollable matrix for many items.
 */
export function AssignmentsTab() {
  const queryClient = useQueryClient();
  const [teamSearch, setTeamSearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');

  // ─── Queries ────────────────────────────────────────────────────────────────

  const { data: teamsData, isLoading: isLoadingTeams } = useQuery({
    queryKey: ['settings', 'assignments', 'teams'],
    queryFn: async () => {
      const response = await apiClient.get<
        ApiResponse<{ teams: Team[]; meta: { page: number; pageSize: number; totalCount: number } }>
      >('/teams?pageSize=200&status=active');
      return response.data.data.teams;
    },
  });

  const { data: dataRolesData, isLoading: isLoadingRoles } = useQuery({
    queryKey: ['settings', 'assignments', 'data-roles'],
    queryFn: async () => {
      const response = await apiClient.get<
        ApiResponse<{
          dataRoles: DataRole[];
          meta: { page: number; pageSize: number; totalCount: number };
        }>
      >('/data-roles?pageSize=200&status=active');
      return response.data.data.dataRoles;
    },
  });

  const teams = teamsData ?? [];
  const dataRoles = dataRolesData ?? [];

  const { data: assignmentsMap, isLoading: isLoadingAssignments } = useQuery({
    queryKey: ['settings', 'assignments', 'matrix', teams.map((t) => t.id)],
    queryFn: async () => {
      const map: Record<string, Set<string>> = {};
      const results = await Promise.all(
        teams.map(async (team) => {
          const response = await apiClient.get<ApiResponse<TeamDataRole[]>>(
            `/teams/${team.id}/data-roles`,
          );
          return { teamId: team.id, roles: response.data.data };
        }),
      );
      for (const { teamId, roles } of results) {
        map[teamId] = new Set(roles.map((r) => r.dataRoleId));
      }
      return map;
    },
    enabled: teams.length > 0,
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const assignMutation = useMutation({
    mutationFn: async ({ teamId, dataRoleId }: { teamId: string; dataRoleId: string }) => {
      await apiClient.post(`/teams/${teamId}/data-roles`, { data_role_id: dataRoleId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'assignments', 'matrix'] });
    },
  });

  const unassignMutation = useMutation({
    mutationFn: async ({ teamId, dataRoleId }: { teamId: string; dataRoleId: string }) => {
      await apiClient.delete(`/teams/${teamId}/data-roles/${dataRoleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'assignments', 'matrix'] });
    },
  });

  // ─── Derived State ──────────────────────────────────────────────────────────

  const isLoading = isLoadingTeams || isLoadingRoles || isLoadingAssignments;
  const isMutating = assignMutation.isPending || unassignMutation.isPending;

  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    const lower = teamSearch.toLowerCase();
    return teams.filter((t) => t.name.toLowerCase().includes(lower));
  }, [teams, teamSearch]);

  const filteredRoles = useMemo(() => {
    if (!roleSearch.trim()) return dataRoles;
    const lower = roleSearch.toLowerCase();
    return dataRoles.filter((r) => r.name.toLowerCase().includes(lower));
  }, [dataRoles, roleSearch]);

  const isAssigned = useMemo(() => {
    return (teamId: string, dataRoleId: string): boolean => {
      return assignmentsMap?.[teamId]?.has(dataRoleId) ?? false;
    };
  }, [assignmentsMap]);

  const handleToggle = (teamId: string, dataRoleId: string) => {
    if (isMutating) return;
    if (isAssigned(teamId, dataRoleId)) {
      unassignMutation.mutate({ teamId, dataRoleId });
    } else {
      assignMutation.mutate({ teamId, dataRoleId });
    }
  };

  // Stats
  const totalAssignments = useMemo(() => {
    if (!assignmentsMap) return 0;
    return Object.values(assignmentsMap).reduce((sum, set) => sum + set.size, 0);
  }, [assignmentsMap]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Team ↔ Data Role Assignments"
          description="Toggle assignments to control which data roles apply to each team. Changes take effect immediately."
        />
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
            </div>
          ) : teams.length === 0 || dataRoles.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {teams.length === 0 && dataRoles.length === 0
                  ? 'No active teams or data roles found. Create teams and data roles first.'
                  : teams.length === 0
                    ? 'No active teams found. Create a team in the Teams tab first.'
                    : 'No active data roles found. Create a data role in the Data Roles tab first.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary & Filters */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {totalAssignments}
                  </span>{' '}
                  assignment{totalAssignments !== 1 ? 's' : ''} across{' '}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {teams.length}
                  </span>{' '}
                  team{teams.length !== 1 ? 's' : ''} and{' '}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {dataRoles.length}
                  </span>{' '}
                  role{dataRoles.length !== 1 ? 's' : ''}
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    placeholder="Filter teams..."
                    className="rounded-md border border-border bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100 dark:placeholder:text-gray-500 w-40"
                  />
                  <input
                    type="text"
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    placeholder="Filter roles..."
                    className="rounded-md border border-border bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100 dark:placeholder:text-gray-500 w-40"
                  />
                </div>
              </div>

              {/* Matrix */}
              <div className="relative overflow-hidden rounded-lg border border-border dark:border-border-dark">
                <div className="max-h-[60vh] overflow-auto">
                  <table
                    className="w-full border-collapse"
                    aria-label="Team and data role assignments matrix"
                  >
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-gray-50 dark:bg-gray-800">
                        <th className="sticky left-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400 min-w-[140px] border-b border-r border-border dark:border-border-dark">
                          Team
                        </th>
                        {filteredRoles.map((role) => (
                          <th
                            key={role.id}
                            className="px-2 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 min-w-[100px] max-w-[140px] border-b border-border dark:border-border-dark"
                          >
                            <div className="flex flex-col items-center gap-0.5" title={role.name}>
                              <span className="line-clamp-2 leading-tight">{role.name}</span>
                              {role.isFullAccess && (
                                <span className="rounded bg-blue-100 px-1 py-0.5 text-[9px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                  FULL
                                </span>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border dark:divide-border-dark">
                      {filteredTeams.map((team) => {
                        const teamRoleCount = assignmentsMap?.[team.id]?.size ?? 0;
                        return (
                          <tr
                            key={team.id}
                            className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30"
                          >
                            <td className="sticky left-0 z-10 bg-white px-4 py-2.5 border-r border-border dark:border-border-dark dark:bg-gray-900">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {team.name}
                                </span>
                                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                  {teamRoleCount}
                                </span>
                              </div>
                            </td>
                            {filteredRoles.map((role) => {
                              const assigned = isAssigned(team.id, role.id);
                              return (
                                <td key={role.id} className="px-2 py-2.5 text-center">
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={assigned}
                                    aria-label={`${assigned ? 'Remove' : 'Assign'} ${role.name} ${assigned ? 'from' : 'to'} ${team.name}`}
                                    onClick={() => handleToggle(team.id, role.id)}
                                    disabled={isMutating}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${assigned ? 'bg-brand-600 dark:bg-brand-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                                  >
                                    <span
                                      className={`pointer-events-none inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out ${assigned ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
                                    />
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filteredTeams.length === 0 && (
                  <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No teams match your filter.
                  </p>
                )}
                {filteredRoles.length === 0 && filteredTeams.length > 0 && (
                  <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No roles match your filter.
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
